import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  HostListener,
  ElementRef,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
import { TourService } from '@core/services/tour.service';
import { ActiveTimerService } from '@core/services/active-timer.service';
import { PipTimerService } from '@core/services/pip-timer.service';

const POSITION_KEY = 'ezwork_timer_position';

@Component({
  selector: 'app-floating-timer',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './floating-timer.component.html',
  styleUrl: './floating-timer.component.scss'
})
export class FloatingTimerComponent implements OnInit, OnDestroy {
  private readonly tourService = inject(TourService);
  readonly activeTimerSvc = inject(ActiveTimerService);
  readonly pipTimerSvc = inject(PipTimerService);
  private readonly elRef = inject(ElementRef);

  expanded = signal(false);
  isStopping = signal(false);

  /** Visible whenever a timer is running (or during the tour pip demo step) */
  readonly visible = computed(
    () => this.activeTimerSvc.activeTimer() !== null || this.isPipTourStep()
  );

  readonly isPipTourStep = computed(
    () => this.tourService.isActive() && this.tourService.pipDemoMode()
  );

  /** Budget color state: 'normal' | 'warning' (90%+) | 'danger' (100%+) */
  readonly budgetColorClass = computed(() => {
    const pct = this.activeTimerSvc.progressPercent();
    if (pct >= 100) return 'danger';
    if (pct >= 90) return 'warning';
    return '';
  });

  /** Demo counter for tour step */
  demoElapsed = signal<number>(1427);
  private demoTickerSub?: Subscription;

  // Drag state
  isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private posX = 0;
  private posY = 0;
  private hasMoved = false;

  constructor() {
    effect(() => {
      if (this.isPipTourStep()) {
        this.demoElapsed.set(1427);
        this.demoTickerSub = interval(1000).subscribe(() =>
          this.demoElapsed.update(t => t + 1)
        );
      } else {
        this.demoTickerSub?.unsubscribe();
        this.demoTickerSub = undefined;
      }
    });

    // Collapse the panel when the timer stops
    effect(() => {
      if (!this.activeTimerSvc.activeTimer()) {
        this.expanded.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.restorePosition();
  }

  ngOnDestroy(): void {
    this.demoTickerSub?.unsubscribe();
  }

  private restorePosition(): void {
    try {
      const saved = localStorage.getItem(POSITION_KEY);
      if (!saved) return;
      const { x, y } = JSON.parse(saved) as { x: number; y: number };
      const el = this.elRef.nativeElement.querySelector('.floating-timer') as HTMLElement;
      if (el) {
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
      }
    } catch { /* ignore */ }
  }

  get demoFormattedTime(): string {
    const s = this.demoElapsed();
    return `${this.pad(Math.floor(s / 3600))}:${this.pad(Math.floor((s % 3600) / 60))}:${this.pad(s % 60)}`;
  }

  get formattedTime(): string {
    const s = this.activeTimerSvc.elapsedSeconds();
    return `${this.pad(Math.floor(s / 3600))}:${this.pad(Math.floor((s % 3600) / 60))}:${this.pad(s % 60)}`;
  }

  get projectName(): string {
    return this.activeTimerSvc.activeTimer()?.projectName ?? '';
  }

  toggleExpand(): void {
    if (this.hasMoved) return;
    this.expanded.update(v => !v);
  }

  togglePauseResume(): void {
    if (this.activeTimerSvc.isPaused()) {
      this.activeTimerSvc.resume();
    } else {
      this.activeTimerSvc.pause();
    }
  }

  async stopTimer(): Promise<void> {
    if (this.isStopping()) return;
    this.isStopping.set(true);
    try {
      await this.activeTimerSvc.stop();
    } finally {
      this.isStopping.set(false);
    }
  }

  // --- Drag ---
  onDragStart(event: MouseEvent): void {
    this.isDragging = true;
    this.hasMoved = false;
    const el = this.elRef.nativeElement.querySelector('.floating-timer');
    const rect = el.getBoundingClientRect();
    this.dragStartX = event.clientX - rect.left;
    this.dragStartY = event.clientY - rect.top;
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDragMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.hasMoved = true;
    this.posX = event.clientX - this.dragStartX;
    this.posY = event.clientY - this.dragStartY;
    const el = this.elRef.nativeElement.querySelector('.floating-timer') as HTMLElement;
    if (el) {
      el.style.left = `${this.posX}px`;
      el.style.top = `${this.posY}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    }
  }

  @HostListener('document:mouseup')
  onDragEnd(): void {
    if (this.isDragging) {
      this.isDragging = false;
      try {
        localStorage.setItem(POSITION_KEY, JSON.stringify({ x: this.posX, y: this.posY }));
      } catch { /* ignore */ }
      setTimeout(() => (this.hasMoved = false), 0);
    }
  }

  // --- Document PiP ---
  async togglePip(): Promise<void> {
    if (this.pipTimerSvc.isPipActive()) {
      this.pipTimerSvc.closePip();
    } else {
      await this.pipTimerSvc.openPip();
    }
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
